import { createSelector } from '@reduxjs/toolkit';
import { getOfferWithMinPricingSummary, mapCancellationTypeByCancellation } from 'gql/service/graph/activityService';
import { CancellationType } from 'gql/autogen/CancellationType';
import { CmsValue, Experiments } from 'core/type/BootstrapConfig.types';
import { selectConfigFromBootstrap, selectPrerenderStateFromBootstrap } from 'store/slice/bootstrap';
import { selectIsCalendarLoading, selectIsSoldOut } from 'component/page/detail/store/calendar.selectors';
import { selectActivityRepresentativeInfo } from 'component/page/detail/store/detail.selectors';
import { PriceDisplayBehavior } from 'core/type/behaviors.types';
import { navigateToHashWithTabBarOffset, navigateToHashWithOffset } from 'component/common/DetailInViewWrapper/navigateToHashWithTabBarOffset';
import { selectAvailabilityIsLoading, selectCheapestAvailableOffer } from 'component/page/detail/store/availabilityDEPRECATED.selectors';
import { NavigationHashEnum } from 'core/type/Details.types';
import { PricingSummary } from 'gql/autogen/PricingSummary';
import { RepresentativeInfo } from 'gql/autogen/RepresentativeInfo';
import { Offer } from 'gql/autogen/Offer';
import { setIsScrolling } from 'component/page/detail/store/details';
import { selectTabs } from 'component/page/detail/DetailPageTabs/DetailPageTabs.selectors';
import { isB, isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { ExperimentId } from 'core/type/ExperimentId';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesSeeOptionButtonClickEventFieldsV2 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesSeeOptionButtonClickEventManifestV2 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import React from 'react';
import { focusElementByDataElementNameSelector } from 'core/utils/domUtils';
import { closeOverlay, OverlayType } from 'store/slice/overlay';
import { PACKAGE_OPTIONS_HREF } from 'component/page/detail/DetailPageModularize/contentRegistry.type';
import { OVERLAY_CLOSE_DELAY, NON_TAB_BAR_OFFSET } from 'core-framework/constant/DetailPageModularConstant';

interface OwnProps {
    isOverlay?: boolean;
}

interface StateProps {
    cms: CmsValue;
    cancellationMessage?: string;
    isDisabled: boolean;
    isLoading: boolean;
    isSchedulesEmpty: boolean;
    showPriceWithPointsOnNextLine: boolean;
    pricingSummary?: PricingSummary;
    cheapestPricingSummary?: PricingSummary;
    packageOptionsHref: string;
    isPrerender: boolean;
    showInclusiveTax: boolean;
    isImproveUnavailableOffer: boolean;
    experiments: Experiments;
    activityId: number;
    isDetailPageRefactor: boolean;
}

export interface DispatchProps {
    onSeeOptionsButtonClick: (packageOptionsHref: string, isSidePanel: boolean, isOverlay: boolean) => void;
    handleSeeOptionsButtonClick: (
        seeOptionsButtonRef: React.RefObject<HTMLButtonElement>,
        packageOptionsHref: string,
        isSidePanel: boolean,
        isOverlay: boolean,
        onSeeOptionsButtonClick: (packageOptionsHref: string, isSidePanel: boolean, isOverlay: boolean) => void,
    ) => void;
}

export type AvailbleDetailPriceProps = OwnProps & StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectAvailabilityIsLoading,
    selectIsCalendarLoading,
    selectIsSoldOut,
    selectTabs,
    selectPrerenderStateFromBootstrap,
    selectActivityRepresentativeInfo,
    selectCheapestAvailableOffer,
    (config, isAvailabilityLoading, isCalendarLoading, isSchedulesEmpty, tabsObject, isPrerender, representativeInfo?: RepresentativeInfo, cheapestOffer?: Offer): StateProps => {
        const cms = config.cms;
        const isLoading = isCalendarLoading || isAvailabilityLoading;
        const cancellationType = mapCancellationTypeByCancellation(representativeInfo?.cancellationPolicy);
        const pricingSummary = representativeInfo?.pricingSummary;
        const { tabs } = tabsObject;
        const packageOptionsTab = tabs.find((tab) => tab.id === NavigationHashEnum.PackageOptionsSection) || { href: '' };
        const hideFreeCancellation = config?.behavior?.common?.hideFreeCancellation;
        const showInclusiveTax = config.behavior.common.showInclusiveTax;
        const isImproveUnavailableOffer = isOn(FeatureName.ImproveUnavailableOffer, config);
        const activityId = representativeInfo?.activityId || cheapestOffer?.activityId || 0;
        const isDetailPageRefactor = isB(ExperimentId.ACT_4944, config.experiments);
        const isDetailPageForSimCardEnabled = isOn(FeatureName.DetailPageForSimCard, config);

        return {
            cms,
            isLoading,
            isSchedulesEmpty,
            pricingSummary,
            isPrerender,
            activityId,
            cheapestPricingSummary: getOfferWithMinPricingSummary(cheapestOffer?.enrichedOfferOption || [])?.pricingSummary,
            cancellationMessage: cancellationType === CancellationType.FREE_CANCELLATION && !hideFreeCancellation ? config.cms[144284] : undefined,
            isDisabled: !isImproveUnavailableOffer && isSchedulesEmpty && !isLoading,
            showPriceWithPointsOnNextLine: config.behavior.common.price.displayBehavior === PriceDisplayBehavior.PriceWithPointsOnNextLine,
            packageOptionsHref: isDetailPageForSimCardEnabled ? PACKAGE_OPTIONS_HREF : packageOptionsTab.href,
            showInclusiveTax,
            isImproveUnavailableOffer,
            experiments: config.experiments,
            isDetailPageRefactor,
        };
    },
);

export const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
    onSeeOptionsButtonClick: (packageOptionsHref: string, isSidePanel: boolean, isOverlay: boolean) => {
        if (isOverlay) {
            dispatch(closeOverlay(OverlayType.InfoOverlay));
            setTimeout(() => {
                navigateToHashWithOffset(dispatch, `#${packageOptionsHref}`, setIsScrolling, NON_TAB_BAR_OFFSET, true);
            }, OVERLAY_CLOSE_DELAY);
        } else {
            navigateToHashWithTabBarOffset(dispatch, `#${packageOptionsHref}`, setIsScrolling, true);
            if (!isSidePanel) {
                const isFocusSuccess = focusElementByDataElementNameSelector('activities-available-option-card-choose-button');
                if (!isFocusSuccess) {
                    focusElementByDataElementNameSelector('activities-details-package-option-change-date');
                }
            }
        }
    },
    handleSeeOptionsButtonClick: (
        seeOptionsButtonRef: React.RefObject<HTMLButtonElement>,
        packageOptionsHref: string,
        isSidePanel: boolean,
        isOverlay: boolean,
        onSeeOptionsButtonClick: (packageOptionsHref: string, isSidePanel: boolean, isOverlay: boolean) => void,
    ) => {
        trackElementEvent<ActivitiesSeeOptionButtonClickEventFieldsV2>(ActivitiesSeeOptionButtonClickEventManifestV2.ids, {});
        onSeeOptionsButtonClick(packageOptionsHref, isSidePanel, isOverlay);
        seeOptionsButtonRef.current?.blur();
    },
});

export const getButtonState = (isImproveUnavailableOffer: boolean, isLoadingOrPrerender: boolean, isDisabled: boolean) => {
    if (isImproveUnavailableOffer) {
        if (isLoadingOrPrerender) {
            return 'loading';
        }
        if (isDisabled) {
            return 'disabled';
        }
        return 'enabled';
    }
    if (isDisabled) {
        return 'disabled';
    }
    if (isLoadingOrPrerender) {
        return 'loading';
    }
    return 'enabled';
};
